import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';

const TARGETS = {
  students: {
    label: 'Students',
    endpoint: '/students',
    fields: [
      { key: 'f_name', label: 'First Name', required: true, aliases: ['first name', 'firstname', 'first', 'given name'] },
      { key: 'l_name', label: 'Last Name', required: true, aliases: ['last name', 'lastname', 'last', 'surname', 'family name'] },
      { key: 'grade', label: 'Grade', required: true, aliases: ['grade level', 'class grade', 'year'] },
      { key: 'gender', label: 'Gender', required: true, aliases: ['sex'] },
      { key: 'status', label: 'Status', defaultValue: 'active', aliases: ['student status'] },
      { key: 'dob', label: 'Date of Birth', aliases: ['date of birth', 'birthdate', 'birthday'] },
      { key: 'student_email', label: 'Student Email', aliases: ['email', 'student email'] },
      { key: 'student_phone', label: 'Student Phone', aliases: ['phone', 'student phone', 'mobile'] },
      { key: 'street_address', label: 'Street Address', aliases: ['address', 'street', 'home address'] },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'zip_code', label: 'ZIP Code', aliases: ['zip', 'postal code', 'zipcode'] },
      { key: 'emergency_contact_f_name', label: 'Emergency Contact First Name', aliases: ['emergency first name'] },
      { key: 'emergency_contact_l_name', label: 'Emergency Contact Last Name', aliases: ['emergency last name'] },
      { key: 'emergency_contact_phone', label: 'Emergency Contact Phone', aliases: ['emergency phone'] },
      { key: 'emergency_contact_relationship', label: 'Emergency Contact Relationship', aliases: ['emergency relationship'] },
      { key: 'sis_student_id', label: 'SIS Student ID', aliases: ['student id', 'sis id', 'external id'] },
      { key: 'sis_source', label: 'SIS Source', aliases: ['source'] },
    ],
  },
  teachers: {
    label: 'Teachers',
    endpoint: '/staff',
    fields: [
      { key: 'f_name', label: 'First Name', required: true, aliases: ['first name', 'firstname', 'first', 'given name'] },
      { key: 'l_name', label: 'Last Name', required: true, aliases: ['last name', 'lastname', 'last', 'surname', 'family name'] },
      { key: 'mobile_phone', label: 'Mobile Phone', required: true, aliases: ['phone', 'mobile', 'cell', 'telephone'] },
      { key: 'school_email', label: 'School Email', aliases: ['email', 'teacher email', 'staff email'] },
      { key: 'position_title', label: 'Position Title', defaultValue: 'Teacher', aliases: ['title', 'role', 'job title'] },
      { key: 'assigned_grades', label: 'Assigned Grades', aliases: ['grades', 'grade coverage', 'assigned classes'] },
      { key: 'is_active', label: 'Active', defaultValue: '1', aliases: ['active', 'status'] },
    ],
  },
};

const REQUIRED_STYLE = {
  color: '#b91c1c',
  fontSize: 12,
  fontWeight: 700,
  marginLeft: 6,
};
const STUDENT_STATUSES = ['active', 'inactive', 'graduate'];
const STUDENT_GENDERS = ['M', 'F'];

function normalizeHeader(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ');
}

function parseCsv(text) {
  const rows = [];
  let current = [];
  let value = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        value += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      current.push(value);
      value = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') index += 1;
      current.push(value);
      if (current.some((cell) => String(cell).trim() !== '')) {
        rows.push(current);
      }
      current = [];
      value = '';
      continue;
    }

    value += char;
  }

  current.push(value);
  if (current.some((cell) => String(cell).trim() !== '')) {
    rows.push(current);
  }

  if (rows.length === 0) {
    return { headers: [], records: [] };
  }

  const headers = rows[0].map((header, index) => (
    String(header || `Column ${index + 1}`).replace(/^\uFEFF/, '').trim() || `Column ${index + 1}`
  ));
  const records = rows.slice(1).map((row, rowIndex) => {
    const record = { __rowNumber: rowIndex + 2 };
    headers.forEach((header, index) => {
      record[header] = String(row[index] || '').trim();
    });
    return record;
  });

  return { headers, records };
}

function guessColumn(field, headers) {
  const candidates = [field.label, field.key, ...(field.aliases || [])].map(normalizeHeader);
  const normalizedHeaders = headers.map((header) => ({ header, normalized: normalizeHeader(header) }));
  const exact = normalizedHeaders.find((item) => candidates.includes(item.normalized));
  if (exact) return exact.header;

  const loose = normalizedHeaders.find((item) => (
    candidates.some((candidate) => item.normalized.includes(candidate) || candidate.includes(item.normalized))
  ));
  return loose?.header || '';
}

function buildInitialMappings(targetKey, headers) {
  const config = TARGETS[targetKey];
  return Object.fromEntries(config.fields.map((field) => [
    field.key,
    {
      column: guessColumn(field, headers),
      manual: field.defaultValue || '',
    },
  ]));
}

function normalizeDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) {
    return direct.toISOString().slice(0, 10);
  }

  const match = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (!match) return raw;

  const month = match[1].padStart(2, '0');
  const day = match[2].padStart(2, '0');
  const year = match[3].length === 2 ? `20${match[3]}` : match[3];
  return `${year}-${month}-${day}`;
}

function normalizeGender(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  if (['m', 'male', 'boy'].includes(raw)) return 'M';
  if (['f', 'female', 'girl'].includes(raw)) return 'F';
  return value;
}

function normalizeStudentStatus(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'active';

  if (['active', 'a', 'current', 'currently enrolled', 'enrolled', 'yes', 'y', '1', 'true'].includes(raw)) {
    return 'active';
  }

  if (['inactive', 'i', 'withdrawn', 'not active', 'no', 'n', '0', 'false'].includes(raw)) {
    return 'inactive';
  }

  if (['graduate', 'graduated', 'grad', 'alumni'].includes(raw)) {
    return 'graduate';
  }

  return raw;
}

function normalizeBooleanNumber(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  if (['1', 'true', 'yes', 'y', 'active'].includes(raw)) return 1;
  if (['0', 'false', 'no', 'n', 'inactive'].includes(raw)) return 0;
  return value;
}

function normalizeGradeAliasToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function addGradeAlias(map, alias, gradeCode) {
  const normalized = normalizeGradeAliasToken(alias);
  const parsedGradeCode = Number(gradeCode);
  if (!normalized || !Number.isInteger(parsedGradeCode)) return;
  if (!map.has(normalized)) map.set(normalized, parsedGradeCode);
}

function parseGradeAliases(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).map((item) => item.trim()).filter(Boolean);

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String).map((item) => item.trim()).filter(Boolean);
  } catch {
    return [];
  }

  return [];
}

function buildGradeAliasMap(gradeLevels, schoolGradeNames) {
  const map = new Map([
    ['daycare', -3],
    ['dc', -3],
    ['nursery', -2],
    ['n', -2],
    ['pre k', -1],
    ['pre-k', -1],
    ['prek', -1],
    ['prekindergarten', -1],
    ['pk', -1],
    ['kindergarten', 0],
    ['kinder', 0],
    ['k', 0],
  ]);

  gradeLevels.forEach((gradeLevel) => {
    const gradeCode = Number(gradeLevel.grade_code);
    addGradeAlias(map, gradeLevel.canonical_name, gradeCode);
    addGradeAlias(map, gradeLevel.canonical_abbrev, gradeCode);
    addGradeAlias(map, `grade ${gradeCode}`, gradeCode);
    addGradeAlias(map, `g${gradeCode}`, gradeCode);
  });

  schoolGradeNames
    .filter((gradeName) => Number(gradeName.is_active) !== 0)
    .forEach((gradeName) => {
      const gradeCode = Number(gradeName.grade_code);
      addGradeAlias(map, gradeName.label, gradeCode);
      parseGradeAliases(gradeName.aliases).forEach((alias) => addGradeAlias(map, alias, gradeCode));
    });

  return map;
}

function parseGradeValue(value, gradeAliasMap = new Map()) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const direct = Number(raw);
  if (Number.isInteger(direct)) return direct;

  const normalized = normalizeGradeAliasToken(raw);
  if (gradeAliasMap.has(normalized)) return gradeAliasMap.get(normalized);

  const coded = normalized.match(/^(?:g|grade|gr)\s*(-?\d+)$/);
  if (coded) return Number(coded[1]);

  const ordinal = normalized.match(/^(\d+)(?:st|nd|rd|th)(?: grade)?$/);
  if (ordinal) return Number(ordinal[1]);

  return Number(raw);
}

function parseGrades(value, gradeAliasMap = new Map()) {
  const raw = String(value || '').trim();
  if (!raw) return null;

  return raw
    .split(/[;,|]/)
    .map((item) => parseGradeValue(item, gradeAliasMap))
    .filter((item) => Number.isInteger(item));
}

function getMappedValue(row, mapping) {
  const csvValue = mapping.column ? String(row[mapping.column] || '').trim() : '';
  const manualValue = String(mapping.manual || '').trim();
  return csvValue || manualValue;
}

function normalizePayloadField(targetKey, fieldKey, rawValue, options = {}) {
  if (targetKey === 'students' && fieldKey === 'grade') {
    return rawValue === '' ? '' : parseGradeValue(rawValue, options.gradeAliasMap);
  }

  if (targetKey === 'students' && fieldKey === 'gender') {
    return normalizeGender(rawValue);
  }

  if (targetKey === 'students' && fieldKey === 'status') {
    return normalizeStudentStatus(rawValue);
  }

  if (targetKey === 'students' && fieldKey === 'dob') {
    return normalizeDate(rawValue);
  }

  if (targetKey === 'teachers' && fieldKey === 'assigned_grades') {
    return parseGrades(rawValue, options.gradeAliasMap);
  }

  if (targetKey === 'teachers' && fieldKey === 'is_active') {
    return normalizeBooleanNumber(rawValue);
  }

  return rawValue || null;
}

function applyPayloadDefaults(targetKey, payload) {
  if (targetKey === 'students') {
    payload.status = normalizeStudentStatus(payload.status || 'active');
  }

  if (targetKey === 'teachers') {
    payload.permission_level = 'TEACHER';
    payload.is_active = payload.is_active === undefined || payload.is_active === '' ? 1 : payload.is_active;
  }

  return payload;
}

function buildPayload(targetKey, schoolId, row, mappings, options = {}) {
  const payload = { school_id: Number(schoolId) };
  const fields = TARGETS[targetKey].fields;

  fields.forEach((field) => {
    const rawValue = getMappedValue(row, mappings[field.key] || {});
    if (!rawValue && !field.required) return;
    payload[field.key] = normalizePayloadField(targetKey, field.key, rawValue, options);
  });

  return applyPayloadDefaults(targetKey, payload);
}

function buildPayloadFromDraft(targetKey, schoolId, draft, options = {}) {
  const payload = { school_id: Number(schoolId) };

  TARGETS[targetKey].fields.forEach((field) => {
    const rawValue = String(draft[field.key] ?? '').trim();
    if (!rawValue && !field.required) return;
    payload[field.key] = normalizePayloadField(targetKey, field.key, rawValue, options);
  });

  return applyPayloadDefaults(targetKey, payload);
}

function formatDraftValue(targetKey, fieldKey, value) {
  if (fieldKey === 'assigned_grades' && Array.isArray(value)) {
    return value.join(', ');
  }

  if (targetKey === 'students' && fieldKey === 'status') {
    return normalizeStudentStatus(value);
  }

  if (targetKey === 'students' && fieldKey === 'gender') {
    return normalizeGender(value);
  }

  return value ?? '';
}

function buildRetryDraft(targetKey, payload) {
  return Object.fromEntries(TARGETS[targetKey].fields.map((field) => [
    field.key,
    formatDraftValue(targetKey, field.key, payload[field.key]),
  ]));
}

function getMissingRequired(targetKey, row, mappings) {
  return TARGETS[targetKey].fields
    .filter((field) => field.required)
    .filter((field) => !getMappedValue(row, mappings[field.key] || {}))
    .map((field) => field.label);
}

export default function CsvImport({ token, navigate }) {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [gradeLevels, setGradeLevels] = useState([]);
  const [schoolGradeNames, setSchoolGradeNames] = useState([]);
  const [targetKey, setTargetKey] = useState('students');
  const [headers, setHeaders] = useState([]);
  const [records, setRecords] = useState([]);
  const [mappings, setMappings] = useState(() => buildInitialMappings('students', []));
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState([]);
  const [retryDrafts, setRetryDrafts] = useState({});
  const [retryingRows, setRetryingRows] = useState({});

  useEffect(() => {
    apiFetch('/schools', {}, token)
      .then((res) => setSchools(res.data || []))
      .catch((err) => setError(err.message));
  }, [token]);

  useEffect(() => {
    apiFetch('/grade-levels', {}, token)
      .then((res) => setGradeLevels(res.data || []))
      .catch(() => setGradeLevels([]));
  }, [token]);

  useEffect(() => {
    if (!schoolId) {
      setSchoolGradeNames([]);
      return;
    }

    apiFetch(`/school-grade-names?school_id=${schoolId}`, {}, token)
      .then((res) => setSchoolGradeNames(res.data || []))
      .catch(() => setSchoolGradeNames([]));
  }, [schoolId, token]);

  useEffect(() => {
    setMappings(buildInitialMappings(targetKey, headers));
    setResults([]);
    setRetryDrafts({});
    setRetryingRows({});
  }, [headers, targetKey]);

  const selectedConfig = TARGETS[targetKey];
  const gradeAliasMap = useMemo(
    () => buildGradeAliasMap(gradeLevels, schoolGradeNames),
    [gradeLevels, schoolGradeNames]
  );

  const validation = useMemo(() => {
    const missingMappingFields = selectedConfig.fields
      .filter((field) => field.required)
      .filter((field) => {
        const mapping = mappings[field.key] || {};
        return !mapping.column && !String(mapping.manual || '').trim();
      });

    const rowErrors = records.map((row) => ({
      rowNumber: row.__rowNumber,
      missing: getMissingRequired(targetKey, row, mappings),
    })).filter((item) => item.missing.length > 0);

    return { missingMappingFields, rowErrors };
  }, [mappings, records, selectedConfig.fields, targetKey]);

  const canImport = Boolean(schoolId)
    && records.length > 0
    && validation.missingMappingFields.length === 0
    && validation.rowErrors.length === 0
    && !importing;

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setError('');
    setResults([]);
    setRetryDrafts({});
    setRetryingRows({});

    if (!file) return;

    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.headers.length === 0 || parsed.records.length === 0) {
        setError('CSV must include a header row and at least one data row.');
        setHeaders([]);
        setRecords([]);
        setFileName('');
        return;
      }

      setHeaders(parsed.headers);
      setRecords(parsed.records);
      setFileName(file.name);
    } catch (err) {
      setError(err.message || 'Could not parse CSV file.');
    }
  };

  const updateMapping = (fieldKey, patch) => {
    setMappings((current) => ({
      ...current,
      [fieldKey]: {
        ...(current[fieldKey] || { column: '', manual: '' }),
        ...patch,
      },
    }));
  };

  const handleImport = async () => {
    if (!canImport) return;

    setImporting(true);
    setError('');
    setResults([]);
    setRetryDrafts({});
    setRetryingRows({});

    const nextResults = [];
    const nextRetryDrafts = {};

    for (const row of records) {
      const payload = buildPayload(targetKey, schoolId, row, mappings, { gradeAliasMap });
      try {
        await apiFetch(selectedConfig.endpoint, {
          method: 'POST',
          body: JSON.stringify(payload),
        }, token);
        nextResults.push({ rowNumber: row.__rowNumber, success: true, message: 'Imported', payload });
      } catch (err) {
        nextResults.push({ rowNumber: row.__rowNumber, success: false, message: err.message, payload });
        nextRetryDrafts[row.__rowNumber] = buildRetryDraft(targetKey, payload);
      }
      setResults([...nextResults]);
      setRetryDrafts({ ...nextRetryDrafts });
    }

    setImporting(false);
  };

  const updateRetryDraft = (rowNumber, fieldKey, value) => {
    setRetryDrafts((current) => ({
      ...current,
      [rowNumber]: {
        ...(current[rowNumber] || {}),
        [fieldKey]: value,
      },
    }));
  };

  const handleRetryFailedRow = async (result) => {
    const rowNumber = result.rowNumber;
    const draft = retryDrafts[rowNumber] || {};
    const missing = selectedConfig.fields
      .filter((field) => field.required)
      .filter((field) => !String(draft[field.key] ?? '').trim())
      .map((field) => field.label);

    if (missing.length > 0) {
      setError(`Row ${rowNumber} is missing required values: ${missing.join(', ')}`);
      return;
    }

    const payload = buildPayloadFromDraft(targetKey, schoolId, draft, { gradeAliasMap });
    setError('');
    setRetryingRows((current) => ({ ...current, [rowNumber]: true }));

    try {
      await apiFetch(selectedConfig.endpoint, {
        method: 'POST',
        body: JSON.stringify(payload),
      }, token);

      setResults((current) => current.map((item) => (
        item.rowNumber === rowNumber
          ? { ...item, success: true, message: 'Imported after correction', payload }
          : item
      )));
      setRetryDrafts((current) => {
        const next = { ...current };
        delete next[rowNumber];
        return next;
      });
    } catch (err) {
      setResults((current) => current.map((item) => (
        item.rowNumber === rowNumber
          ? { ...item, success: false, message: err.message, payload }
          : item
      )));
      setRetryDrafts((current) => ({
        ...current,
        [rowNumber]: buildRetryDraft(targetKey, payload),
      }));
    } finally {
      setRetryingRows((current) => {
        const next = { ...current };
        delete next[rowNumber];
        return next;
      });
    }
  };

  const previewRows = records.slice(0, 8);
  const importedCount = results.filter((result) => result.success).length;
  const failedCount = results.filter((result) => !result.success).length;
  const failedResults = results.filter((result) => !result.success);

  return (
    <div>
      <div className="sa-breadcrumb">
        <button onClick={() => navigate('dashboard')}>Schools</button>
        {' / '}
        CSV Import
      </div>

      <div className="sa-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <h2 style={{ marginBottom: 4 }}>CSV Import</h2>
            <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
              Import students or teachers by mapping CSV columns to app fields.
            </p>
          </div>
          <button className="sa-btn sa-btn-secondary" onClick={() => navigate('dashboard')}>
            Back
          </button>
        </div>

        {error && <div className="sa-error">{error}</div>}

        <div className="sa-form-grid">
          <div className="sa-field">
            <label>School *</label>
            <select value={schoolId} onChange={(event) => setSchoolId(event.target.value)} required>
              <option value="">Select school...</option>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.school_nickname || school.legal_name}
                </option>
              ))}
            </select>
          </div>

          <div className="sa-field">
            <label>Import Type *</label>
            <select value={targetKey} onChange={(event) => setTargetKey(event.target.value)}>
              {Object.entries(TARGETS).map(([key, target]) => (
                <option key={key} value={key}>{target.label}</option>
              ))}
            </select>
          </div>

          <div className="sa-field full-width">
            <label>CSV File *</label>
            <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
            {fileName ? (
              <span style={{ color: '#64748b', fontSize: 12, marginTop: 6, display: 'block' }}>
                {fileName}: {records.length} row{records.length === 1 ? '' : 's'}, {headers.length} column{headers.length === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {headers.length > 0 ? (
        <div className="sa-card" style={{ marginTop: 16 }}>
          <h2 style={{ marginBottom: 12 }}>Map Fields</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="sa-table">
              <thead>
                <tr>
                  <th>App Field</th>
                  <th>CSV Column</th>
                  <th>Manual / Default Value</th>
                </tr>
              </thead>
              <tbody>
                {selectedConfig.fields.map((field) => {
                  const mapping = mappings[field.key] || { column: '', manual: '' };
                  return (
                    <tr key={field.key}>
                      <td style={{ minWidth: 220, fontWeight: 600 }}>
                        {field.label}
                        {field.required ? <span style={REQUIRED_STYLE}>Required</span> : null}
                      </td>
                      <td>
                        <select
                          value={mapping.column}
                          onChange={(event) => updateMapping(field.key, { column: event.target.value })}
                          style={{ width: '100%', minWidth: 180 }}
                        >
                          <option value="">Not mapped</option>
                          {headers.map((header) => (
                            <option key={header} value={header}>{header}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          value={mapping.manual}
                          onChange={(event) => updateMapping(field.key, { manual: event.target.value })}
                          placeholder={field.required ? 'Required if CSV value is blank' : 'Optional fallback'}
                          style={{ width: '100%', minWidth: 220 }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {validation.missingMappingFields.length > 0 ? (
            <div className="sa-error" style={{ marginTop: 14 }}>
              Required fields need a CSV column or manual value: {validation.missingMappingFields.map((field) => field.label).join(', ')}
            </div>
          ) : null}

          {validation.rowErrors.length > 0 ? (
            <div className="sa-error" style={{ marginTop: 14 }}>
              {validation.rowErrors.length} row{validation.rowErrors.length === 1 ? '' : 's'} still have missing required values.
              First issue: row {validation.rowErrors[0].rowNumber} missing {validation.rowErrors[0].missing.join(', ')}.
            </div>
          ) : null}
        </div>
      ) : null}

      {previewRows.length > 0 ? (
        <div className="sa-card" style={{ marginTop: 16 }}>
          <h2 style={{ marginBottom: 12 }}>Preview</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Row</th>
                  {selectedConfig.fields.filter((field) => field.required).map((field) => (
                    <th key={field.key}>{field.label}</th>
                  ))}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => {
                  const missing = getMissingRequired(targetKey, row, mappings);
                  return (
                    <tr key={row.__rowNumber}>
                      <td>{row.__rowNumber}</td>
                      {selectedConfig.fields.filter((field) => field.required).map((field) => (
                        <td key={field.key}>{getMappedValue(row, mappings[field.key] || {}) || '-'}</td>
                      ))}
                      <td>
                        {missing.length === 0 ? (
                          <span className="sa-badge sa-badge-active">Ready</span>
                        ) : (
                          <span className="sa-badge sa-badge-suspended">Missing {missing.join(', ')}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="sa-btn-row" style={{ alignItems: 'center' }}>
            <button className="sa-btn sa-btn-primary" disabled={!canImport} onClick={handleImport}>
              {importing ? 'Importing...' : `Import ${records.length} ${selectedConfig.label}`}
            </button>
            <button className="sa-btn sa-btn-secondary" disabled={importing} onClick={() => navigate('dashboard')}>
              Cancel
            </button>
            {!canImport && !importing ? (
              <span style={{ color: '#64748b', fontSize: 13 }}>
                Select a school, upload a CSV, and complete all required mappings to import.
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {results.length > 0 ? (
        <div className="sa-card" style={{ marginTop: 16 }}>
          <h2 style={{ marginBottom: 12 }}>Import Results</h2>
          <p style={{ marginTop: 0, color: '#64748b' }}>
            Imported {importedCount}. Failed {failedCount}.
          </p>
          <div style={{ overflowX: 'auto', maxHeight: 320 }}>
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Result</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result) => (
                  <tr key={result.rowNumber}>
                    <td>{result.rowNumber}</td>
                    <td>
                      <span className={`sa-badge ${result.success ? 'sa-badge-active' : 'sa-badge-suspended'}`}>
                        {result.success ? 'Imported' : 'Failed'}
                      </span>
                    </td>
                    <td>{result.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {failedResults.length > 0 ? (
        <div className="sa-card" style={{ marginTop: 16 }}>
          <h2 style={{ marginBottom: 12 }}>Fix Failed Inserts</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Error</th>
                  <th>Correct Values</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {failedResults.map((result) => {
                  const rowNumber = result.rowNumber;
                  const draft = retryDrafts[rowNumber] || buildRetryDraft(targetKey, result.payload || {});

                  return (
                    <tr key={rowNumber}>
                      <td style={{ verticalAlign: 'top', fontWeight: 700 }}>{rowNumber}</td>
                      <td style={{ verticalAlign: 'top', minWidth: 220 }}>{result.message}</td>
                      <td style={{ minWidth: 520 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                          {selectedConfig.fields.map((field) => {
                            const value = draft[field.key] ?? '';
                            const label = (
                              <span style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>
                                {field.label}
                                {field.required ? <span style={REQUIRED_STYLE}>Required</span> : null}
                              </span>
                            );

                            if (targetKey === 'students' && field.key === 'status') {
                              return (
                                <label key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {label}
                                  <select
                                    value={STUDENT_STATUSES.includes(String(value)) ? String(value) : ''}
                                    onChange={(event) => updateRetryDraft(rowNumber, field.key, event.target.value)}
                                  >
                                    <option value="">Select...</option>
                                    {STUDENT_STATUSES.map((status) => (
                                      <option key={status} value={status}>{status}</option>
                                    ))}
                                  </select>
                                </label>
                              );
                            }

                            if (targetKey === 'students' && field.key === 'gender') {
                              return (
                                <label key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {label}
                                  <select
                                    value={STUDENT_GENDERS.includes(String(value)) ? String(value) : ''}
                                    onChange={(event) => updateRetryDraft(rowNumber, field.key, event.target.value)}
                                  >
                                    <option value="">Select...</option>
                                    {STUDENT_GENDERS.map((gender) => (
                                      <option key={gender} value={gender}>{gender}</option>
                                    ))}
                                  </select>
                                </label>
                              );
                            }

                            if (targetKey === 'teachers' && field.key === 'is_active') {
                              return (
                                <label key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {label}
                                  <select
                                    value={String(value) === '0' ? '0' : '1'}
                                    onChange={(event) => updateRetryDraft(rowNumber, field.key, event.target.value)}
                                  >
                                    <option value="1">Yes</option>
                                    <option value="0">No</option>
                                  </select>
                                </label>
                              );
                            }

                            return (
                              <label key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {label}
                                <input
                                  type={field.key === 'grade' ? 'number' : 'text'}
                                  value={value}
                                  onChange={(event) => updateRetryDraft(rowNumber, field.key, event.target.value)}
                                />
                              </label>
                            );
                          })}
                        </div>
                      </td>
                      <td style={{ verticalAlign: 'top', minWidth: 120 }}>
                        <button
                          type="button"
                          className="sa-btn sa-btn-primary"
                          disabled={Boolean(retryingRows[rowNumber])}
                          onClick={() => handleRetryFailedRow(result)}
                        >
                          {retryingRows[rowNumber] ? 'Reinserting...' : 'Reinsert'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
